import React from "react";
import { Modal, Box, IconButton } from "@mui/joy";
import { keyframes } from "@emotion/react";
import ReactConfetti from "react-confetti";
import { CloseIcon, CasinoIcon, GroupsIcon, TrophyIcon, MailIcon } from "../../styles/StyledMUI";
import { zekayStaticBranding } from "../../config/staticFiles";
import { useCompanyBrandLogoSrc } from "../../utils/useCompanyBrandLogoSrc";
import { useEvent } from "../../contexts/api";
import DarkTypography from "./DarkTypography";
import { colorTokens, withAlpha } from "../../styles/globalStyles";
import type { Winner, Event, UpdateEventRequest, EventParticipant } from "api/events/Event.typed";

interface RandomDrawerProps {
  participants: { id: number; name: string; votes: number }[];
  winners?: Winner[];
  event: Event;
  open: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

const slideInFromLeft = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

type DrawingState = "initial" | "counting" | "countdown" | "drawing" | "result";

interface InitialPhaseProps {
  onStart: () => void;
  disabled: boolean;
}

const InitialPhase = ({ onStart, disabled }: InitialPhaseProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 2,
      animation: disabled ? "none" : `${pulse} 2s infinite`,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.4 : 1,
    }}
    onClick={() => {
      if (!disabled) onStart();
    }}
  >
    <CasinoIcon sx={{ fontSize: { xs: 50, sm: 80 }, color: colorTokens.text.primary }} />
    <DarkTypography
      level="h1"
      sx={{ fontSize: { xs: "2rem", sm: "3rem", md: "4rem" }, textAlign: "center" }}
    >
      {disabled ? "Pas assez de participants" : "Prêts ?"}
    </DarkTypography>
  </Box>
);

interface CountingPhaseProps {
  participantsDisplay: number;
  ballotsDisplay: number;
  participantsTotal: number;
  ballotsTotal: number;
}

const CountingPhase = ({
  participantsDisplay,
  ballotsDisplay,
  participantsTotal,
  ballotsTotal,
}: CountingPhaseProps) => (
  <Box sx={{ display: "flex", gap: { xs: 3, sm: 6 } }}>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <GroupsIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: colorTokens.text.primary }} />
      <DarkTypography level="h1" sx={{ fontSize: { xs: "2.5rem", sm: "4rem" } }}>
        {participantsDisplay}
      </DarkTypography>
      <DarkTypography level="h3" colorVariant="secondary" sx={{ opacity: 0.8 }}>
        {participantsTotal === 1 ? "participant" : "participants"}
      </DarkTypography>
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <MailIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: colorTokens.text.primary }} />
      <DarkTypography
        level="h1"
        sx={{ fontSize: { xs: "2.5rem", sm: "4rem" }, animation: `${pulse} 0.5s infinite` }}
      >
        {ballotsDisplay}
      </DarkTypography>
      <DarkTypography level="h3" colorVariant="secondary" sx={{ opacity: 0.8 }}>
        {ballotsTotal === 1 ? "bulletin" : "bulletins"}
      </DarkTypography>
    </Box>
  </Box>
);

const CountdownPhase = ({ count }: { count: number }) => (
  <DarkTypography
    level="h1"
    sx={{ fontSize: { xs: "4rem", sm: "6rem", md: "8rem" }, animation: `${pulse} 1s infinite` }}
  >
    {count}
  </DarkTypography>
);

const DrawingPhase = ({ name, isDrawing }: { name: string; isDrawing: boolean }) => (
  <Box
    sx={{
      minHeight: 200,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <DarkTypography
      level="h1"
      sx={{
        fontSize: isDrawing
          ? { xs: "1.5rem", sm: "2.5rem", md: "3rem" }
          : { xs: "2rem", sm: "3rem", md: "4rem" },
        transform: isDrawing ? "scale(1.1)" : "scale(1)",
        transition: "all 0.3s ease-in-out",
        textAlign: "center",
        maxWidth: "80vw",
        wordWrap: "break-word",
        animation: isDrawing ? "none" : `${pulse} 2s ease-in-out`,
      }}
    >
      {name}
    </DarkTypography>
  </Box>
);

const ResultPhase = ({ name, showLogo }: { name: string; showLogo: boolean }) => {
  const { width, height } = useWindowSize();
  const { logoSrc, onLogoImgError, hasCompanyLogo } = useCompanyBrandLogoSrc();
  const fallbackSrc = zekayStaticBranding.escapadeLogoWebp();
  const displaySrc = hasCompanyLogo ? logoSrc : fallbackSrc;

  return (
    <>
      <DrawingPhase name={name} isDrawing={false} />
      {showLogo && (
        <>
          <ReactConfetti width={width} height={height} recycle={true} numberOfPieces={200} />
          <Box
            sx={{
              position: "absolute",
              bottom: "10%",
              left: 0,
              right: 0,
              animation: `${slideInFromLeft} 1s ease-out`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 4 } }}>
              <TrophyIcon
                sx={{ fontSize: { xs: 40, sm: 60, md: 80 }, color: colorTokens.accent.primary }}
              />
              <Box
                component="img"
                src={displaySrc}
                alt="Logo"
                onError={hasCompanyLogo ? onLogoImgError : undefined}
                sx={{ width: { xs: "200px", sm: "300px", md: "400px" }, height: "auto" }}
              />
              <TrophyIcon
                sx={{ fontSize: { xs: 40, sm: 60, md: 80 }, color: colorTokens.accent.primary }}
              />
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

const RandomDrawer: React.FC<RandomDrawerProps> = ({
  participants,
  winners = [],
  event,
  open,
  onClose,
  onRefresh,
}) => {
  const { updateEventSilent } = useEvent();
  const [currentName, setCurrentName] = React.useState<string>("");
  const [drawingState, setDrawingState] = React.useState<DrawingState>("initial");
  const [showLogo, setShowLogo] = React.useState(false);
  const [displayBallots, setDisplayBallots] = React.useState(0);
  const [displayParticipants, setDisplayParticipants] = React.useState(0);
  const [countdown, setCountdown] = React.useState(4);
  const isShuffling = React.useRef(true);
  const hasRecordedWinner = React.useRef(false);

  const ballotBox = React.useMemo(() => {
    const winnerIds = new Set(winners?.map((w) => w.id) || []);
    const eligibleParticipants = participants.filter((p) => !winnerIds.has(p.id));
    const ballots = eligibleParticipants.flatMap((participant) =>
      Array(participant.votes).fill({ id: participant.id, name: participant.name }),
    );
    return ballots;
  }, [participants, winners]);

  const totalBallots = React.useMemo(() => ballotBox.length, [ballotBox]);

  const participantsCount = React.useMemo(() => {
    const winnerIds = new Set(winners?.map((w) => w.id) || []);
    return participants.filter((p) => !winnerIds.has(p.id)).length;
  }, [participants, winners]);

  React.useEffect(() => {
    if (drawingState === "counting" && participantsCount < 2) {
      setDrawingState("initial");
      return;
    }

    if (
      drawingState === "counting" &&
      (displayBallots < totalBallots || displayParticipants < participantsCount)
    ) {
      const ANIMATION_DURATION = 5000;
      const STEPS = 50;
      const INTERVAL = ANIMATION_DURATION / STEPS;
      const INCREMENT = Math.max(1, Math.ceil(totalBallots / STEPS));
      const INCREMENT_PART = Math.max(1, Math.ceil(participantsCount / STEPS));

      const timeout = setTimeout(() => {
        setDisplayBallots((prev) => Math.min(prev + INCREMENT, totalBallots));
        setDisplayParticipants((prev) => Math.min(prev + INCREMENT_PART, participantsCount));
      }, INTERVAL);

      return () => clearTimeout(timeout);
    } else if (
      drawingState === "counting" &&
      displayBallots >= totalBallots &&
      displayParticipants >= participantsCount
    ) {
      const timeout = setTimeout(() => setDrawingState("countdown"), 3000);
      return () => clearTimeout(timeout);
    }
  }, [drawingState, displayBallots, displayParticipants, totalBallots, participantsCount]);

  React.useEffect(() => {
    if (drawingState === "countdown" && countdown > 0) {
      const timeout = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
      return () => clearTimeout(timeout);
    } else if (drawingState === "countdown" && countdown === 0) {
      setDrawingState("drawing");
    }
  }, [drawingState, countdown]);

  // Weighted random draw: shuffle names for ANIMATION_DURATION then pick final winner from ballot box
  React.useEffect(() => {
    if (drawingState === "drawing") {
      const ANIMATION_DURATION = 5000;
      const SHUFFLE_INTERVAL = 100;
      const startTime = Date.now();
      isShuffling.current = true;
      let shuffleTimer: NodeJS.Timeout;

      const shuffle = () => {
        if (!isShuffling.current) return;

        const currentTime = Date.now();
        const elapsed = currentTime - startTime;

        if (elapsed < ANIMATION_DURATION) {
          const randomIndex = Math.floor(Math.random() * ballotBox.length);
          setCurrentName(ballotBox[randomIndex].name);
          shuffleTimer = setTimeout(shuffle, SHUFFLE_INTERVAL);
        } else {
          isShuffling.current = false;
          const finalWinner = ballotBox[Math.floor(Math.random() * ballotBox.length)];
          setCurrentName(finalWinner.name);
          setDrawingState("result");
        }
      };

      shuffle();

      return () => {
        isShuffling.current = false;
        if (shuffleTimer) clearTimeout(shuffleTimer);
      };
    }
  }, [ballotBox, drawingState]);

  React.useEffect(() => {
    if (open) {
      isShuffling.current = false;
      setDrawingState("initial");
      setShowLogo(false);
      setDisplayBallots(0);
      setDisplayParticipants(0);
      setCountdown(4);
      setCurrentName("");
    }
  }, [open]);

  React.useEffect(() => {
    if (drawingState === "result" && currentName && !hasRecordedWinner.current) {
      hasRecordedWinner.current = true;

      const winner: EventParticipant = ballotBox.find((b) => b.name === currentName);

      if (!winner) return;

      const newWinner: Winner = {
        id: Number(winner.id),
        first_name: currentName.split(" ")[0],
        last_name: currentName.split(" ").slice(1).join(" "),
        rank: (event.winners?.length || 0) + 1,
      };

      const updateWinner = async () => {
        const updateEventRequest: UpdateEventRequest = {
          id: event.id,
          name: event.name,
          description: event.description || "",
          start_date: event.start_date,
          end_date: event.end_date,
          winners: [...(event.winners || []), newWinner],
        };

        await updateEventSilent(updateEventRequest);
        setShowLogo(true);
      };

      const timer = setTimeout(updateWinner, 500);
      return () => clearTimeout(timer);
    } else if (drawingState !== "result") {
      hasRecordedWinner.current = false;
    }
  }, [ballotBox, drawingState, currentName, event, updateEventSilent]);

  const startDrawing = React.useCallback(async () => {
    await onRefresh();
    setShowLogo(false);
    setDisplayBallots(0);
    setDisplayParticipants(0);
    setCountdown(4);
    setCurrentName("");
    setDrawingState("counting");
    hasRecordedWinner.current = false;
  }, [onRefresh]);

  const handleClose = async () => {
    await onRefresh();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "100dvh",
        p: 0,
        m: 0,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: colorTokens.bg.app,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          color: colorTokens.text.primary,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            display: "flex",
            gap: 2,
            zIndex: 10,
          }}
        >
          <IconButton
            variant="soft"
            onClick={startDrawing}
            disabled={
              (drawingState !== "initial" && drawingState !== "result") || participantsCount < 2
            }
            sx={{
              backgroundColor: withAlpha(colorTokens.text.primary, 0.1),
              color: colorTokens.text.primary,
              "&:hover": { backgroundColor: withAlpha(colorTokens.text.primary, 0.2) },
            }}
          >
            <CasinoIcon />
          </IconButton>
          <IconButton
            variant="soft"
            onClick={handleClose}
            sx={{
              backgroundColor: withAlpha(colorTokens.text.primary, 0.1),
              color: colorTokens.text.primary,
              "&:hover": { backgroundColor: withAlpha(colorTokens.text.primary, 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {drawingState === "initial" && (
          <InitialPhase onStart={startDrawing} disabled={participantsCount < 2} />
        )}
        {drawingState === "counting" && (
          <CountingPhase
            participantsDisplay={displayParticipants}
            ballotsDisplay={displayBallots}
            participantsTotal={participantsCount}
            ballotsTotal={totalBallots}
          />
        )}
        {drawingState === "countdown" && <CountdownPhase count={countdown} />}
        {drawingState === "drawing" && <DrawingPhase name={currentName} isDrawing={true} />}
        {drawingState === "result" && <ResultPhase name={currentName} showLogo={showLogo} />}
      </Box>
    </Modal>
  );
};

function useWindowSize() {
  const [size, setSize] = React.useState({ width: window.innerWidth, height: window.innerHeight });

  React.useEffect(() => {
    const handleResize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return size;
}

export default RandomDrawer;
